import { useEffect, useMemo } from "react";
import { useTheme } from "src/hooks/useTheme";
import { useIsDev } from "src/hooks/useIsDev";
import { useSelector } from "src/redux/hooks";

import { Item } from "./components/Item";
import { Group } from "./components/layout/Group/Group";
import { Panel } from "./components/layout/Panel/Panel";
import styles from "./GeneratorV2.module.scss";
import { useFieldNavigation } from "./hooks/useV2FieldNavigation";
import {
  createGeneratorStore,
  GeneratorStoreProvider,
  useScopedGeneratorStore,
} from "./store";
import { buildGeneratorThemeVars } from "./theme";
import type { V2LogbookDef } from "./types";
import { isV2Group } from "./types";
import { extractPanelEntries } from "./utils/extractPanelEntries";

interface GeneratorV2Options {
  readonly?: boolean;
  disablePanel?: boolean;
  isolatedStore?: boolean;
}

export interface GeneratorV2Props {
  /** Logbook definition (data structure) */
  entryInfo: any;
  logbookDef: V2LogbookDef;
  options?: GeneratorV2Options;
}

/**
 * Root renderer for v2 logbooks.
 * @param logbookDef - Logbook definition (data structure)
 * @returns JSX element with rendered form and panel
 */
export function GeneratorV2({
  entryInfo,
  logbookDef,
  options,
}: GeneratorV2Props) {
  const { isolatedStore = false } = options ?? {};
  const scopedStore = useMemo(
    () => (isolatedStore ? createGeneratorStore() : null),
    [isolatedStore],
  );

  if (scopedStore) {
    return (
      <GeneratorStoreProvider value={scopedStore}>
        <GeneratorV2Content
          entryInfo={entryInfo}
          logbookDef={logbookDef}
          options={options}
        />
      </GeneratorStoreProvider>
    );
  }

  return (
    <GeneratorV2Content
      entryInfo={entryInfo}
      logbookDef={logbookDef}
      options={options}
    />
  );
}

function GeneratorV2Content({
  entryInfo,
  logbookDef,
  options,
}: GeneratorV2Props) {
  const hydrate = useScopedGeneratorStore(s => s.hydrate);
  const refData = useScopedGeneratorStore(s => s.refData);
  const setLoading = useScopedGeneratorStore(s => s.setLoading);
  const setPanelEntries = useScopedGeneratorStore(s => s.setPanelEntries);
  const setEntryInfo = useScopedGeneratorStore(s => s.setEntryInfo);
  const isDev = useIsDev();

  const { readonly = false } = options ?? {};

  useEffect(() => {
    setLoading(true);
    try {
      hydrate(
        { ...logbookDef, isReadonly: readonly ?? logbookDef.isReadonly },
        entryInfo,
      );
      setEntryInfo(entryInfo);
    } finally {
      setLoading(false);
    }
  }, [logbookDef, hydrate, setLoading, setEntryInfo, entryInfo, readonly]);

  useEffect(() => {
    isDev && logbookDef && console.log({ logbookDef, entryInfo });
  }, [logbookDef, entryInfo, isDev]);

  // ── Panel entries ────────────────────────────────────────────────────────
  const panelFeature = refData?.features?.panel;
  const panelEntries = useMemo(
    () => (panelFeature && refData ? extractPanelEntries(refData.content) : []),
    [refData, panelFeature],
  );

  useEffect(() => {
    setPanelEntries(panelEntries);
  }, [panelEntries, setPanelEntries]);

  // ── Theme colors ────────────────────────────────
  const theme = useTheme();
  const c = theme?.colors ?? {};
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const themeVars = {
    ...buildGeneratorThemeVars(c),
    "--gen-text-transform": uppercase ? "uppercase" : "none",
  };

  const { contentRef, handleContentKeyDown } = useFieldNavigation();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.generator} style={themeVars}>
      {/* Side panel */}
      {panelFeature && panelEntries.length > 0 && (
        <Panel entries={panelEntries} align={panelFeature.align} />
      )}

      {/* Content area */}
      <div
        className={styles.content}
        ref={contentRef}
        onKeyDown={handleContentKeyDown}>
        {(refData?.content ?? []).map((node, i) =>
          isV2Group(node) ? (
            <Group key={node.id ?? node.group ?? `group-${i}`} group={node} />
          ) : (
            <Item key={node.id ?? node.item ?? `item-${i}`} item={node} />
          ),
        )}
      </div>
    </div>
  );
}
